import { useCallback, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'

import { QA_NAME_NEXT_BTN } from '../../../../constants/qa'
import { SIZE } from '../../../../constants/size'
import { useUserData } from '../../../../hooks'
import { isStaffUser } from '../../../../utils/user'
import { AIGretaLoader } from '../../../AIGreta'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../Form'
import { Content } from '../../../Modal'
import {
  useAutoCompleteAddressFormFields,
  useRequestWizardDraft,
} from '../../Request.hooks'
import {
  INIT_STAGE,
  REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_BUILDING,
  REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_CONTRACT,
  REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_FLAT,
  REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_USER,
} from '../RequestWizardForm.constants'
import { isFilledWithDweller, isFormFilled } from '../RequestWizardForm.utils'

import { BuildingField } from './BuildingField'
import { ContractIDField } from './ContractIDField'
import { FlatField } from './FlatField'
import { ManagerField } from './ManagerField'
import { UserField } from './UserField'

import * as styles from './RequestWizardInitStageFormFields.module.css'

function RequestWizardInitStageFormFields({
  ContentComponent = Content,
  buildingFieldProps,
  contentComponentProps = {
    size: SIZE.X_LARGE,
  },
  flatFieldProps,
  initialUser,
  isAILoading,
  isCardLoading,
  isSingleFlat,
  notifications,
  setIsSingleFlat,
  userFieldProps,
  onCancel,
  onRemoveNotification,
  onSetNotification,
}) {
  const { dirty, setFieldValue, values } = useFormikContext()

  const { setDraft: setWizardDraft } = useRequestWizardDraft()

  useEffect(() => {
    if (dirty) {
      setWizardDraft({
        chunkSignature: null,
        stage: INIT_STAGE,
        uuid: null,
        values,
      })
    }
  }, [dirty, values])

  const currentUser = useUserData()

  useFormValidationErrorsNotification({
    onRemoveNotification,
    onSetNotification,
  })

  useAutoCompleteAddressFormFields({
    buildingFieldName: REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_BUILDING,
    contractFieldName: REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_CONTRACT,
    currentUser,
    flatFieldName: REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_FLAT,
    setIsSingleFlat,
    userFieldName: REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_USER,
  })

  const isUserFieldFilledWithDweller = isFilledWithDweller(
    values[REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_USER]
  )

  const isFilledAddressDisabled = useMemo(
    () => isFormFilled(values) && isSingleFlat,
    [isSingleFlat, values]
  )

  const isFieldsLoading = isCardLoading && !isAILoading

  const handleCancel = useCallback(() => {
    onCancel({
      dirty,
    })
  }, [dirty, onCancel])

  const handleClearField = useCallback(() => {
    setFieldValue(REQUEST_WIZARD_INIT_STAGE_FIELD_NAME_CONTRACT, null)
  }, [])

  const isStaffUserAcc = isStaffUser(currentUser)

  return (
    <FormBody
      actionsProps={{
        ButtonAdd: false,
        ButtonClear: false,
        UploadComponent: false,
        cancelProps: {
          onClick: handleCancel,
        },
        className: styles.actions,
        submitProps: {
          disabled: isAILoading,
          isLoading: isFieldsLoading,
          qaName: QA_NAME_NEXT_BTN,
          title: 'Modal.WizardNextBtnKey',
        },
      }}
    >
      <ContentComponent {...contentComponentProps}>
        <FormInlineNotifications notifications={notifications} />

        <div className={styles.aiLoaderWrap}>
          {isAILoading ? <AIGretaLoader /> : null}

          <FormFieldsContainer className={styles.formFields}>
            <UserField
              {...userFieldProps}
              initialUser={initialUser}
              isLoading={isFieldsLoading}
              setIsSingleFlat={setIsSingleFlat}
              onClear={handleClearField}
            />
            <BuildingField
              {...buildingFieldProps}
              isFilledAddressDisabled={isFilledAddressDisabled}
              isLoading={isFieldsLoading}
              required={isUserFieldFilledWithDweller}
              onClear={handleClearField}
            />
            <FlatField
              {...flatFieldProps}
              isFilledAddressDisabled={isFilledAddressDisabled}
              isLoading={isFieldsLoading}
              required={isUserFieldFilledWithDweller}
              onClear={handleClearField}
            />
            {isStaffUserAcc ? (
              <ContractIDField isLoading={isFieldsLoading} />
            ) : null}
            {isStaffUserAcc ? (
              <ManagerField isLoading={isFieldsLoading} />
            ) : null}
          </FormFieldsContainer>
        </div>
      </ContentComponent>
    </FormBody>
  )
}

export default RequestWizardInitStageFormFields
